import api from '@/api/_kine-configuration'

const ephemeral = new WeakMap()
const attempts = new WeakMap()
const exits = new WeakMap()
const routeGuards = new WeakMap()
const copy = value => JSON.parse(JSON.stringify(value))
const uuid = () => {
    const bytes = new Uint8Array(16)
    window.crypto.getRandomValues(bytes); bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
const scopes = [
    { value: 'initial', label: 'Bilan initial' }, { value: 'session', label: 'Séance' },
    { value: 'reassessment', label: 'Réévaluation' }, { value: 'final', label: 'Bilan final' }
]
const supportedKinds = ['text', 'number', 'single_choice', 'multiple_choice', 'date', 'file', 'rich_text']
export default {
    name: 'kine-configuration',
    props: { selectedConsultation: { type: Object, required: true } },
    data() {
        return { fiches: [], selectedId: '', draft: null, loading: false, saving: false, error: '', success: '', request: 0, destroyed: false, scopes, preview: false, confirmation: false, baseDraft: '', discardPrompt: false, versions: null, savedVersion: null }
    },
    computed: {
        enabled() { return process.env.VUE_APP_KINE_CONFIGURATION_ENABLED === 'true' },
        autorise() { const role = this.$store.getters.activeRole; return !!(role && role.nom === 'ROLE_ADMINISTRATEUR') },
        identity() {
            const auth = this.$store.getters.authInfo || {}, role = this.$store.getters.activeRole || {}
            return JSON.stringify([auth.id || (auth.user && auth.user.id), auth.access_token, role.id, role.nom])
        },
        selected() { return this.fiches.find(fiche => String(fiche.id) === String(this.selectedId)) || null },
        sessionsAllowed() { return !!(this.selected && this.selected.type_consultation_id != null && this.selected.is_default === false) },
        staleDraft() { return !!(this.draft && this.selected && this.draft.expected_revision !== this.selected.revision) },
        dirty() { return !!(this.draft && JSON.stringify(this.draft) !== this.baseDraft) },
        previewScopes() { return this.scopes.map(scope => Object.assign({}, scope, { sections: this.draft ? this.draft.sections.filter(section => section.scope === scope.value) : [] })) },
        validationErrors() {
            if (!this.draft) return ['Choisissez une fiche.']
            if (!this.selected) return ['Cette fiche n’est plus disponible dans le catalogue. Le brouillon est conservé.']
            const errors = []
            if (this.staleDraft) errors.push(`Votre brouillon est basé sur la version ${this.draft.expected_revision}, mais la version ${this.selected.revision} est maintenant publiée. Reprenez la version courante avant de publier.`)
            if (this.draft.mode === 'sessions' && !this.sessionsAllowed) errors.push('Dupliquez cette fiche avant de configurer le suivi par séances.')
            if (!this.draft.sections.length) errors.push('Ajoutez au moins une rubrique depuis la configuration des sections.')
            if (this.draft.sections.some(section => section.fields.some(field => supportedKinds.indexOf(field.kind) < 0))) errors.push('Un type de champ n’est pas pris en charge. Vérifiez sa définition dans les Paramètres avant de publier.')
            if (this.draft.sections.some(section => !scopes.some(scope => scope.value === section.scope))) errors.push('Chaque rubrique doit avoir un moment défini.')
            return errors
        }
    },
    watch: {
        identity() { this.invalidate() },
        'selectedConsultation.id'() { this.invalidate(); this.loadFiches() }
    },
    mounted() {
        window.addEventListener('beforeunload', this.beforeUnload)
        if (this.$router && this.enabled && this.autorise) routeGuards.set(this, this.$router.beforeEach((to, from, next) => {
            if (this.saving) next(false)
            else this.requestLeave(() => next(), () => next(false))
        }))
        return this.loadFiches()
    },
    beforeDestroy() {
        this.destroyed = true; this.request++; ephemeral.delete(this); attempts.delete(this); this.continueDraft()
        const remove = routeGuards.get(this)
        if (remove) remove()
        routeGuards.delete(this); window.removeEventListener('beforeunload', this.beforeUnload)
    },
    methods: {
        invalidate() {
            this.request++; ephemeral.delete(this); attempts.delete(this)
            this.fiches = []; this.selectedId = ''; this.draft = null; this.confirmation = false
            this.loading = false; this.saving = false; this.error = ''; this.success = ''; this.continueDraft(); this.versions = null; this.savedVersion = null
        },
        async capability() {
            let state = ephemeral.get(this)
            if (state && state.identity === this.identity && state.expires > Date.now()) return state.token
            const identity = this.identity
            const response = await api.getAccess()
            if (!response || response.success !== true || !response.data || !response.data.access_token) throw Error('Capacité absente')
            state = { identity, token: response.data.access_token, expires: Date.now() + Math.max(0, Number(response.data.expires_in || 0) - 20) * 1000 }
            if (!this.destroyed && identity === this.identity) ephemeral.set(this, state)
            return state.token
        },
        async loadFiches() {
            if (!this.enabled || !this.autorise || this.destroyed || this.saving || this.confirmation) return
            const request = ++this.request
            this.loading = true; this.error = ''
            try {
                const token = await this.capability()
                if (this.destroyed || request !== this.request) return
                const response = await api.getFiches(this.selectedConsultation.id, token)
                if (this.destroyed || request !== this.request) return
                if (!response || !response.data || response.data.enabled !== true || !Array.isArray(response.data.fiches)) throw Error('Configuration indisponible')
                this.fiches = response.data.fiches
            } catch (error) {
                if (!this.destroyed && request === this.request) this.error = this.errorMessage(error, false)
            } finally { if (!this.destroyed && request === this.request) this.loading = false }
        },
        chooseFiche(id) {
            if (this.loading || this.saving || this.confirmation) return
            if (String(id) === this.selectedId) return
            this.requestLeave(() => this.applyFiche(id))
        },
        applyFiche(id) {
            this.selectedId = String(id)
            if (!this.selected) { this.draft = null; return }
            const latest = this.selected.latest_version
            const publishedIds = latest ? latest.sections.map(section => section.section_id) : []
            const source = this.selected.sections
            const ordered = publishedIds.map(id => source.find(section => section.section_id === id)).filter(Boolean)
                .concat(source.filter(section => publishedIds.indexOf(section.section_id) < 0))
            this.draft = { expected_revision: this.selected.revision, mode: latest ? latest.mode : 'sessions', sections: ordered.map(section => {
                const saved = latest && latest.sections.find(item => item.section_id === section.section_id)
                return Object.assign(copy(section), { scope: saved ? saved.scope : 'initial', required_fields: saved ? saved.required_fields.slice() : [] })
            }) }
            this.error = ''; this.success = ''; this.preview = false
            this.versions = null; this.savedVersion = null
            this.baseDraft = JSON.stringify(this.draft)
        },
        setRequired(section, key, checked) {
            if (this.saving || this.confirmation || !section.fields.some(field => field.key === key)) return
            section.required_fields = section.required_fields.filter(value => value !== key)
            if (checked) section.required_fields.push(key)
        },
        moveSection(index, direction) {
            if (!this.draft || this.saving || this.confirmation) return
            const target = index + direction
            if (target < 0 || target >= this.draft.sections.length) return
            const section = this.draft.sections.splice(index, 1)[0]
            this.draft.sections.splice(target, 0, section)
        },
        requestPublication() {
            if (!this.enabled || !this.autorise || this.loading || this.saving || this.validationErrors.length) return
            const payload = { expected_revision: this.draft.expected_revision, mode: this.draft.mode, sections: this.draft.sections.map(section => ({
                section_id: section.section_id, scope: section.scope, required_fields: section.required_fields.slice()
            })) }
            const fingerprint = JSON.stringify([this.selected.id, payload])
            const previous = attempts.get(this)
            if (!previous || previous.fingerprint !== fingerprint) attempts.set(this, { ficheId: this.selected.id, fingerprint, payload, key: uuid() })
            this.confirmation = true; this.error = ''
        },
        async publish() {
            if (!this.enabled || !this.autorise || !this.confirmation || this.loading || this.saving || this.destroyed || this.validationErrors.length) return
            const attempt = attempts.get(this), request = ++this.request
            if (!attempt) return
            this.saving = true; this.error = ''; this.success = ''
            try {
                const token = await this.capability()
                if (this.destroyed || request !== this.request || !this.autorise) return
                const response = await api.publishVersion(attempt.ficheId, attempt.payload, token, attempt.key)
                if (this.destroyed || request !== this.request) return
                const version = response && response.data
                if (!version || !version.id || !Number.isInteger(version.revision) || !Array.isArray(version.sections)) throw Error('Publication non confirmée')
                this.$set(this.selected, 'latest_version', version); this.$set(this.selected, 'revision', version.revision)
                this.draft = { expected_revision: version.revision, mode: version.mode, sections: copy(version.sections) }
                this.$set(this.selected, 'sections', version.sections.map(section => ({ section_id: section.section_id, label: section.label, fields: copy(section.fields) })))
                this.baseDraft = JSON.stringify(this.draft)
                this.success = `Version ${version.revision} publiée. Les versions précédentes sont conservées.`
                this.confirmation = false; attempts.delete(this)
            } catch (error) {
                if (!this.destroyed && request === this.request) {
                    this.error = this.errorMessage(error, true)
                }
            } finally { if (!this.destroyed && request === this.request) this.saving = false }
        },
        async loadVersions() {
            if (!this.selected || !this.autorise || this.loading || this.saving || this.confirmation || this.destroyed) return
            const request = ++this.request, ficheId = this.selected.id
            this.loading = true; this.error = ''
            try {
                const token = await this.capability()
                if (this.destroyed || request !== this.request) return
                const response = await api.getVersions(ficheId, token)
                if (this.destroyed || request !== this.request) return
                if (!response || !Array.isArray(response.data)) throw Error('Versions indisponibles')
                this.versions = response.data; this.savedVersion = null
            } catch (error) {
                if (!this.destroyed && request === this.request) this.error = this.errorMessage(error, false)
            } finally { if (!this.destroyed && request === this.request) this.loading = false }
        },
        scopeLabel(value) { const scope = scopes.find(item => item.value === value); return scope ? scope.label : 'Moment non reconnu' },
        requestLeave(callback, cancel) {
            if (this.saving) return
            if (this.dirty || this.confirmation) {
                const previous = exits.get(this)
                if (previous && previous.cancel) previous.cancel()
                exits.set(this, { callback, cancel }); this.discardPrompt = true
            }
            else callback()
        },
        close() { this.requestLeave(() => this.$emit('close')) },
        cancelDialog(event) {
            if (this.saving || (!this.discardPrompt && !this.confirmation)) return
            if (event) { event.stopPropagation(); event.preventDefault() }
            if (this.discardPrompt) this.continueDraft()
            else this.confirmation = false
        },
        restartDraft() { if (!this.loading && !this.saving) this.requestLeave(() => this.applyFiche(this.selectedId)) },
        continueDraft() {
            const pending = exits.get(this)
            this.discardPrompt = false; exits.delete(this)
            if (pending && pending.cancel) pending.cancel()
        },
        discard() {
            if (this.saving) return
            const pending = exits.get(this)
            this.discardPrompt = false; this.confirmation = false; exits.delete(this)
            if (pending) pending.callback()
        },
        beforeUnload(event) {
            if (this.dirty || this.confirmation || this.saving) { event.preventDefault(); event.returnValue = '' }
        },
        errorMessage(error, publication) {
            const status = error.response && error.response.status, data = error.response && error.response.data || {}
            const suffix = this.draft ? ' Le brouillon est conservé.' : ''
            if (status === 401) { ephemeral.delete(this); return 'L’accès à la configuration a expiré. Réessayez pour renouveler cet accès.' + suffix }
            if (status === 403) return 'Votre autorisation ne permet pas cette configuration. Contactez l’administrateur.' + suffix
            if (status === 409) return 'La version a été modifiée ou la référence de publication est en conflit. Actualisez les fiches pour comparer.' + suffix
            if (status === 404 || status === 503) return 'La configuration Kiné est désactivée ou indisponible sur ce serveur local.' + suffix
            if (status === 422) {
                const messages = Object.values(data.errors || {}).flat().filter(value => typeof value === 'string').slice(0, 3)
                return (messages.join(' ') || 'Vérifiez les rubriques et champs : la configuration a été refusée.') + suffix
            }
            return (publication ? 'La publication n’a pas été confirmée. Réessayez cette même publication : sa référence évite un doublon.' : 'Impossible de charger les fiches. Réessayez.') + suffix
        }
    }
}
